import { cn } from '@ai-tutor/ui';
import { longBreakAfter } from '@/lib/focus/focusMachine';

interface RoundStopsProps {
  rounds: number;
  /** Rounds finished so far. */
  done: number;
  /** Where the session is now: a round under way, or the break after one. */
  at: 'round' | 'break' | 'idle';
  /** 0-1 through the round under way. */
  current?: number;
}

const C = 2 * Math.PI * 7;

/**
 * Under the classic ring: a small ring per round, a dot per short break and a
 * wider mark for the long break halfway. Where the session stands, without a
 * bar.
 */
export function RoundStops({ rounds, done, at, current = 0 }: RoundStopsProps) {
  const longAfter = longBreakAfter(rounds);
  const items = [];
  for (let r = 1; r <= rounds; r++) {
    const isNow = at === 'round' && r === done + 1;
    const fill = r <= done ? 1 : isNow ? current : 0;
    items.push(
      <span
        key={`r${r}`}
        className={cn(
          'block h-4 w-4 transition-transform duration-base',
          isNow && 'scale-125'
        )}
      >
        <svg viewBox='0 0 18 18' className='h-full w-full -rotate-90'>
          <circle
            cx='9'
            cy='9'
            r='7'
            fill='none'
            strokeWidth='3'
            className={
              r <= done
                ? 'stroke-brand-500 dark:stroke-brand-300'
                : 'stroke-brand-200/70 dark:stroke-brand-500/20'
            }
          />
          <circle
            cx='9'
            cy='9'
            r='7'
            fill='none'
            strokeWidth='3'
            strokeLinecap='round'
            strokeDasharray={C}
            strokeDashoffset={C * (1 - fill)}
            className='stroke-brand-500 transition-[stroke-dashoffset] duration-slow dark:stroke-brand-300'
          />
        </svg>
      </span>
    );
    if (r < rounds) {
      const isLong = r === longAfter;
      items.push(
        <span
          key={`b${r}`}
          className={cn(
            'block rounded-full',
            isLong ? 'h-1.5 w-5' : 'h-1.5 w-1.5',
            r <= done && !(at === 'break' && r === done)
              ? 'bg-success/70'
              : 'bg-success/20',
            at === 'break' &&
              r === done &&
              'bg-success/70 ring-4 ring-success/15'
          )}
        />
      );
    }
  }
  return (
    <div
      className='flex items-center gap-1.5'
      role='img'
      aria-label={`${done} of ${rounds} rounds done${longAfter ? `, long break after round ${longAfter}` : ''}`}
    >
      {items}
    </div>
  );
}
